export const CONSENT_STORAGE_KEY = "qc_consent";
export const CONSENT_VERSION = "1.0";
export const CONSENT_EXPIRES_IN_DAYS = 180;

export type ConsentCategory = "essential" | "functional" | "analytics" | "monitoring" | "marketing";

export type ConsentCategories = Record<ConsentCategory, boolean>;

export interface ConsentData {
  version: string;
  granted_at: string;
  expires_in_days: number;
  categories: ConsentCategories;
}

const consentCategories = ["essential", "functional", "analytics", "monitoring", "marketing"] as const;

export function buildEssentialConsentCategories(): ConsentCategories {
  return {
    essential: true,
    functional: false,
    analytics: false,
    monitoring: false,
    marketing: false,
  };
}

export function buildAllConsentCategories(): ConsentCategories {
  return {
    essential: true,
    functional: true,
    analytics: true,
    monitoring: true,
    marketing: true,
  };
}

export function normalizeConsentCategories(input?: Partial<ConsentCategories> | null): ConsentCategories {
  const base = buildEssentialConsentCategories();

  if (!input || typeof input !== "object") {
    return base;
  }

  return {
    essential: true,
    functional: input.functional === true,
    analytics: input.analytics === true,
    monitoring: input.monitoring === true,
    marketing: input.marketing === true,
  };
}

export function buildConsentData(
  categories: Partial<ConsentCategories>,
  now = new Date(),
): ConsentData {
  return {
    version: CONSENT_VERSION,
    granted_at: now.toISOString(),
    expires_in_days: CONSENT_EXPIRES_IN_DAYS,
    categories: normalizeConsentCategories(categories),
  };
}

export function isConsentExpired(consentData: ConsentData, now = Date.now()) {
  const grantedAt = new Date(consentData.granted_at).getTime();
  if (!Number.isFinite(grantedAt)) return true;

  const expiresInDays =
    Number.isFinite(consentData.expires_in_days) && consentData.expires_in_days > 0
      ? consentData.expires_in_days
      : CONSENT_EXPIRES_IN_DAYS;

  return now - grantedAt > expiresInDays * 24 * 60 * 60 * 1000;
}

export function parseConsentData(value: unknown, now = Date.now()): ConsentData | null {
  if (!value || typeof value !== "object") return null;

  const candidate = value as Record<string, unknown>;
  if (candidate.version !== CONSENT_VERSION) return null;
  if (typeof candidate.granted_at !== "string") return null;

  const categories = candidate.categories;
  if (!categories || typeof categories !== "object") return null;

  const normalizedCategories = normalizeConsentCategories(categories as Partial<ConsentCategories>);
  const consentData: ConsentData = {
    version: CONSENT_VERSION,
    granted_at: candidate.granted_at,
    expires_in_days:
      typeof candidate.expires_in_days === "number" && Number.isFinite(candidate.expires_in_days)
        ? candidate.expires_in_days
        : CONSENT_EXPIRES_IN_DAYS,
    categories: normalizedCategories,
  };

  if (isConsentExpired(consentData, now)) return null;
  return consentData;
}

function getBrowserStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readConsentData(storage: Storage | null = getBrowserStorage(), now = Date.now()) {
  if (!storage) return null;

  try {
    const rawValue = storage.getItem(CONSENT_STORAGE_KEY);
    if (!rawValue) return null;
    return parseConsentData(JSON.parse(rawValue), now);
  } catch {
    return null;
  }
}

export function writeConsentData(
  categories: Partial<ConsentCategories>,
  storage: Storage | null = getBrowserStorage(),
  now = new Date(),
) {
  const consentData = buildConsentData(categories, now);
  if (!storage) return consentData;

  storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
  return consentData;
}

export function clearConsentData(storage: Storage | null = getBrowserStorage()) {
  storage?.removeItem(CONSENT_STORAGE_KEY);
}

export function isConsentCategory(value: unknown): value is ConsentCategory {
  return typeof value === "string" && consentCategories.includes(value as ConsentCategory);
}
